import { forwardRef, Fragment, memo } from 'react';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useKeyboardTooltip } from '../../../hooks/useKeyboardTooltip';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../../locale/formatting/useFormatLocale';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { InvisibleText } from '../../InvisibleText';
import bundle from '../NovaComponents-Calendar-gui-icu.properties';
import cn from '../Calendar.css.json';
import '../Calendar.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useCalendarTodayId = createUseHtmlId('Calendar-today');
const useCalendarSpecialDayId = createUseHtmlId('Calendar-special-day');
const useCalendarNonWorkDayId = createUseHtmlId('Calendar-non-workday');
const useTextLocalizer = createUseTextLocalizer(bundle);
export const DayCell = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function DayCell(props, ref) {
  const {
    today,
    specialDay,
    nonWorkDay,
    ariaDescribedBy,
    ariaLabel,
    testId,
    focused,
    focusVisible,
    disabled,
    selected,
    inInterval,
    inCurrentMonth,
    singleSelection,
    intervalSelection,
    specialDayAriaLabel,
    nonWorkDayAriaLabel,
    onPress,
    onBlur,
    day,
    children
  } = props;
  const invisibleTodayId = useCalendarTodayId();
  const invisibleSpecialDayId = useCalendarSpecialDayId();
  const invisibleNonWorkDayId = useCalendarNonWorkDayId();
  const locale = useFormatLocale();
  const localize = useTextLocalizer();
  const getDayCellAriaDescribedBy = () => {
    const dayCellAriaDescribedBy = [];
    if (today) {
      dayCellAriaDescribedBy.push(invisibleTodayId);
    }
    if (specialDay) {
      dayCellAriaDescribedBy.push(invisibleSpecialDayId);
    }
    if (nonWorkDay) {
      dayCellAriaDescribedBy.push(invisibleNonWorkDayId);
    }
    dayCellAriaDescribedBy.push(ariaDescribedBy);
    return dayCellAriaDescribedBy.join(' ');
  };
  const getSpecialDayText = () => specialDayAriaLabel ?? localize('specialDay.aria');
  const getNonWorkDayText = () => nonWorkDayAriaLabel ?? localize('weekend.aria');
  const getMultiSelectMessage = () => localize('multiSelect.msg');
  const getIntervalSelectMessage = () => localize('intervalSelect.msg');
  const getTooltip = () => {
    let tooltip = '';
    if (specialDay) {
      tooltip = getSpecialDayText();
    }
    if (nonWorkDay) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${getNonWorkDayText()}`;
      } else {
        tooltip = getNonWorkDayText();
      }
    }
    if (!singleSelection) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${getMultiSelectMessage()}`;
      } else {
        tooltip = getMultiSelectMessage();
      }
    } else if (intervalSelection) {
      if (tooltip.length > 0) {
        tooltip += `\n\n${getIntervalSelectMessage()}`;
      } else {
        tooltip = getIntervalSelectMessage();
      }
    }
    return tooltip;
  };
  const setKeyboardTooltipRef = useKeyboardTooltip({
    tooltip: getTooltip()
  });
  const getTitle = () => {
    let title = '';
    if (specialDay) {
      title = getSpecialDayText();
    }
    if (nonWorkDay) {
      if (title.length > 0) {
        title += `\n${getNonWorkDayText()}`;
      } else {
        title = getNonWorkDayText();
      }
    }
    return title;
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      role: "gridcell",
      "data-testid": testId,
      ref: el => {
        setKeyboardTooltipRef(el);
        setRef(ref, el);
      },
      className: cx(cn.dayCell, !inCurrentMonth && cn.dayCellExternal, today && cn.dayCellToday, focused && focusVisible && cn.dayCellFocused, !disabled && selected && cn.dayCellSelected, !disabled && selected && inInterval && cn.dayCellInterval, disabled && (locale === 'zh-CN' || locale === 'zh-TW' || locale === 'zh-CN-pseudo' ? cn.dayCellDisabledCn : cn.dayCellDisabled), nonWorkDay && inCurrentMonth && cn.nonWorkDay),
      tabIndex: focused ? 0 : -1,
      "aria-label": inCurrentMonth ? ariaLabel : undefined,
      "aria-describedby": getDayCellAriaDescribedBy(),
      "aria-selected": disabled ? undefined : selected,
      "aria-disabled": disabled || undefined,
      onClick: event => {
        if (event.button === 0) {
          onPress(day);
        }
      },
      onKeyDown: event => {
        switch (event.key) {
          case ' ':
          case 'Spacebar':
          case 'ArrowUp':
          case 'ArrowDown':
          case 'PageUp':
          case 'PageDown':
            event.preventDefault();
            break;
        }
      },
      onBlur: onBlur,
      title: getTitle(),
      children: specialDay ? /*#__PURE__*/_jsx("div", {
        className: cx(cn.specialDay, selected && cn.selected),
        children: /*#__PURE__*/_jsx("span", {
          className: cn.dayCellContent,
          children: children
        })
      }) : /*#__PURE__*/_jsx("span", {
        className: cn.dayCellContent,
        children: children
      })
    }), today && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTodayId,
      testId: postfixTestId(testId, '-today'),
      children: localize('today.description.aria')
    }), specialDay && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleSpecialDayId,
      children: specialDayAriaLabel ?? localize('specialDay.aria')
    }), nonWorkDay && /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleNonWorkDayId,
      children: nonWorkDayAriaLabel ?? localize('weekend.aria')
    })]
  });
}));
//# sourceMappingURL=DayCell.js.map